<template>
  <IconField :class="fieldClasses">
    <slot name="pre">
      <InputIcon>
        <MaterialIcon :ligature="preIconClass" />
      </InputIcon>
    </slot>
    <InputText
      ref="inputRef"
      :aria-label="ariaLabel"
      :class="inputClasses"
      :model-value="modelValue"
      :onpaste="handlePaste"
      :placeholder="placeholder"
      :pt="pt"
      :size="inputSize"
      fluid
      @click="handleClick"
      @update:model-value="handleInput"
      @keyup.enter="handleEnter"
    />
    <slot name="post" />
  </IconField>
</template>

<script lang="ts" setup>
import { watchDebounced } from '@vueuse/core'
import { IconField, InputIcon, InputText } from 'primevue'
import { computed, useTemplateRef } from 'vue'

import MaterialIcon from '@/components/atoms/icons/material-icon/MaterialIcon.vue'

import { TWN_SEARCH_INPUT_DEFAULTS, type TwnSearchInputProps } from './TwnSearchInput.types'

const props = withDefaults(defineProps<TwnSearchInputProps>(), TWN_SEARCH_INPUT_DEFAULTS)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | undefined): void
  (e: 'click', event: MouseEvent): void
  (e: 'enter', value: string | undefined): void
  (e: 'paste', value: string): void
  (e: 'change', value: string): void
}>()

// PrimeVue has no `medium`: its default size is the medium one.
const inputSize = computed(() => (props.size === 'medium' ? undefined : props.size))

const modelValue = defineModel<string | undefined>()

const inputEl = useTemplateRef<InstanceType<typeof InputText> & { $el: HTMLInputElement }>('inputRef')

const handleInput = (value: string | null | undefined) => {
  modelValue.value = value === null ? undefined : value
}

const handlePaste = (e: ClipboardEvent) => {
  emit('paste', (e.target as HTMLInputElement).value)
}

const handleClick = (event: MouseEvent) => {
  if (props.emitOnClick) {
    emit('click', event)
  }
}

const handleEnter = () => {
  emit('enter', modelValue.value)
}

watchDebounced(
  modelValue,
  () => {
    emit('change', modelValue.value ?? '')
  },
  { debounce: props.debounceMs }
)

defineExpose({
  focus: () => {
    inputEl.value?.$el.focus()
  },
  getInputElement: (): HTMLInputElement | undefined => {
    return inputEl.value?.$el
  }
})
</script>
